import { AuthCard } from "@/components/auth/AuthCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { authClient } from "@/lib/auth/client";
import { KeyRound, ShieldCheck } from "lucide-react";
import * as React from "react";
import { Link, useNavigate } from "react-router-dom";

export default function TwoFactorPage() {
  const navigate = useNavigate();
  const [method, setMethod] = React.useState<"totp" | "backup">("totp");
  const [totpCode, setTotpCode] = React.useState("");
  const [backupCode, setBackupCode] = React.useState("");
  const [trustDevice, setTrustDevice] = React.useState(true);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const result =
        method === "totp"
          ? await authClient.twoFactor.verifyTotp({
              code: totpCode.trim(),
              trustDevice,
            })
          : await authClient.twoFactor.verifyBackupCode({
              code: backupCode.trim(),
              trustDevice,
            });

      if (result?.error) {
        setError(result.error.message ?? "Unable to verify two-factor code.");
        return;
      }

      await authClient.getSession();
      navigate("/", { replace: true });
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Unable to verify two-factor code.");
    } finally {
      setLoading(false);
    }
  };

  const activeCode = method === "totp" ? totpCode : backupCode;

  return (
    <AuthCard
      title="Two-factor verification"
      description="Enter the code from your authenticator app or a backup code."
      footer={
        <p>
          Need to restart?{" "}
          <Link className="text-primary font-medium hover:underline" to="/auth/login">
            Back to login
          </Link>
        </p>
      }
    >
      <div className="mb-4 flex gap-2">
        <Button
          type="button"
          size="sm"
          variant={method === "totp" ? "default" : "outline"}
          onClick={() => setMethod("totp")}
        >
          <ShieldCheck className="size-4" />
          Authenticator
        </Button>
        <Button
          type="button"
          size="sm"
          variant={method === "backup" ? "default" : "outline"}
          onClick={() => setMethod("backup")}
        >
          <KeyRound className="size-4" />
          Backup code
        </Button>
      </div>

      <form className="space-y-4" onSubmit={onSubmit}>
        {method === "totp" ? (
          <div className="space-y-2">
            <Label htmlFor="totpCode">Authenticator code</Label>
            <div className="relative">
              <ShieldCheck className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <Input
                id="totpCode"
                value={totpCode}
                onChange={(event) => setTotpCode(event.target.value)}
                autoComplete="one-time-code"
                inputMode="numeric"
                placeholder="123456"
                required
                className="pl-9 tracking-widest"
              />
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <Label htmlFor="backupCode">Backup code</Label>
            <div className="relative">
              <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <Input
                id="backupCode"
                value={backupCode}
                onChange={(event) => setBackupCode(event.target.value)}
                autoComplete="one-time-code"
                placeholder="ABCD-1234"
                required
                className="pl-9 tracking-wider"
              />
            </div>
          </div>
        )}

        <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none">
          <input
            type="checkbox"
            checked={trustDevice}
            onChange={(event) => setTrustDevice(event.target.checked)}
            className="size-4 rounded border-input"
          />
          Trust this device for 30 days
        </label>

        {error ? (
          <p role="alert" aria-live="polite" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </p>
        ) : null}

        <Button type="submit" className="w-full" disabled={loading || activeCode.trim().length < 6}>
          {loading ? <Spinner size="sm" className="text-primary-foreground" /> : null}
          <span translate="no">{loading ? "Verifying..." : "Verify"}</span>
        </Button>
      </form>
    </AuthCard>
  );
}
